import { useEffect, useRef, useState } from 'react';
import { computeMediaHasFailed, computeMediaIsPending } from '../utils/normalizeMediaStatus';
import useIsPageVisible from './useIsPageVisible';

const POLL_DELAYS = [5000, 7000, 10000, 15000, 20000, 30000];
const SLOW_THRESHOLD_MS = 90000;

const useMediaProcessingStatus = ({ mediaId, isPending, hasError, fetchStatus, onSettled }) => {
  const [pending, setPending] = useState(!!isPending);
  const [failed, setFailed] = useState(!!hasError);
  const [isSlow, setIsSlow] = useState(false);
  const isVisible = useIsPageVisible();

  const attemptRef = useRef(0);
  const elapsedRef = useRef(0);
  const timeoutIdRef = useRef(null);
  const isCancelledRef = useRef(false);
  const fetchStatusRef = useRef(fetchStatus);
  const onSettledRef = useRef(onSettled);
  fetchStatusRef.current = fetchStatus;
  onSettledRef.current = onSettled;

  useEffect(() => {
    setPending(!!isPending);
    setFailed(!!hasError);
  }, [isPending, hasError]);

  useEffect(() => {
    attemptRef.current = 0;
    elapsedRef.current = 0;
  }, [mediaId, pending, failed]);

  useEffect(() => {
    if (!mediaId || !fetchStatusRef.current || !pending || failed || !isVisible) {
      return undefined;
    }

    isCancelledRef.current = false;

    const poll = () => {
      const delay = POLL_DELAYS[Math.min(attemptRef.current, POLL_DELAYS.length - 1)];
      attemptRef.current += 1;
      elapsedRef.current += delay;

      timeoutIdRef.current = setTimeout(() => {
        fetchStatusRef.current()
          .then((data) => {
            if (isCancelledRef.current) return;

            if (!computeMediaIsPending(data)) {
              setFailed(computeMediaHasFailed(data));
              setPending(false);
              onSettledRef.current?.(data);
              return;
            }

            if (elapsedRef.current >= SLOW_THRESHOLD_MS) {
              setIsSlow(true);
            }

            poll();
          })
          .catch(() => {
            if (isCancelledRef.current) return;
            setFailed(true);
          });
      }, delay);
    };

    poll();

    return () => {
      isCancelledRef.current = true;
      clearTimeout(timeoutIdRef.current);
    };
  }, [mediaId, pending, failed, isVisible]);

  return { isPending: pending, isSlow, hasError: failed };
};

export default useMediaProcessingStatus;
